import { useCallback, useEffect, useState } from 'react';
import { getSurveyInvite, submitSurveyInvite } from '../core/api';
import { Field, Shell, Unavailable } from './PublicFormPage';

/*
 * The survey respondent page — docs/anonymous-responses-scoping-2026-10.md.
 * Reached at `/survey/<token>`, one single-use link per person.
 *
 * What is deliberate here:
 *
 * 1. **It sends no cookie and shows no one's name.** The link is the whole
 *    authority; the page never learns who holds it and neither does the answer.
 *
 * 2. **It does not say "anonymous".** The sentence it shows is the one the
 *    design can keep: the answer is stored without a name and only ever shown
 *    in groups of five or more. The stronger word waits on a legal read
 *    (scoping doc §7, decision 3).
 *
 * 3. **One sentence for every "no".** A dead link shows the server's own
 *    generic message verbatim and does not guess why.
 *
 * 4. **A failed submit does not clear the form**, and a validation message is
 *    shown as the server wrote it, because an invalid answer does not spend
 *    the link.
 */
export function SurveyPage({ token }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);

  useEffect(() => {
    let live = true;
    getSurveyInvite(token)
      .then((f) => { if (live) setForm(f); })
      .catch((e) => { if (live) setError(e.message || 'This link is not available.'); });
    return () => { live = false; };
  }, [token]);

  const set = useCallback((k, v) => setValues((p) => ({ ...p, [k]: v })), []);

  const submit = async () => {
    setBusy(true);
    setSubmitErrors([]);
    try {
      await submitSurveyInvite(token, values);
      setDone(true);
    } catch (e) {
      const list = e.body && Array.isArray(e.body.errors) ? e.body.errors : [];
      setSubmitErrors(list.length ? list : [e.message || "That didn't send — mind trying again?"]);
      // A 404 means the link is spent or gone; nothing left to retry.
      if (e.status === 404) setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Unavailable message={error} />;
  if (!form) {
    return (
      <Shell>
        <p className="text-center text-sm text-pencil dark:text-sand">Loading…</p>
      </Shell>
    );
  }
  if (done) {
    return (
      <Shell>
        <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-6 text-center">
          <h1 className="font-display text-xl text-ink dark:text-paper">Thank you</h1>
          <p className="mt-2 text-sm text-pencil dark:text-sand">
            Your response has been recorded. This link can’t be used again.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-5 md:p-6">
        <h1 className="font-display text-lg text-ink dark:text-paper">{form.title}</h1>
        <p className="mt-2 text-sm text-pencil dark:text-sand">
          Your answers are stored without your name and are only ever shown in groups of five or more people.
        </p>
        <div className="mt-5 space-y-4">
          {form.fields.map((spec) => (
            <Field key={spec.key} spec={spec} value={values[spec.key]} onChange={set} disabled={busy} />
          ))}
        </div>
        {submitErrors.length > 0 && (
          <ul role="alert" className="mt-4 space-y-1 text-sm text-rose-600 dark:text-rose-400">
            {submitErrors.map((m) => <li key={m}>{m}</li>)}
          </ul>
        )}
        <button type="button" onClick={submit} disabled={busy}
                className="mt-5 w-full rounded-lg bg-ink text-paper dark:bg-paper dark:text-ink px-4 py-2 text-sm font-medium disabled:opacity-50">
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </Shell>
  );
}
